<template>
  <Transition name="drawer">
    <div v-if="visible" class="drawer-overlay" @click.self="$emit('close')">
      <aside class="drawer" role="dialog" aria-label="Filtros">
        <header class="drawer-header">
          <h2 class="drawer-title">Filtros</h2>
          <button class="drawer-close" @click="$emit('close')" aria-label="Fechar">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
          </button>
        </header>

        <div class="drawer-body">
          <section
            v-for="section in sections"
            :key="section.key"
            class="filter-section"
          >
            <h3 class="filter-section-title">{{ section.title }}</h3>
            <label
              v-for="opt in section.options"
              :key="opt.value"
              class="filter-option"
              :class="{ active: filters[section.key] === opt.value }"
            >
              <input
                type="radio"
                :name="section.key"
                :value="opt.value"
                :checked="filters[section.key] === opt.value"
                @change="select(section.key, opt.value)"
              />
              <span class="opt-dot"></span>
              <span class="opt-label">{{ opt.label }}</span>
              <span v-if="opt.count !== undefined" class="opt-count">{{ opt.count }}</span>
            </label>
          </section>
        </div>

        <footer class="drawer-footer">
          <button class="btn-link" @click="clearAll">Limpar filtros</button>
          <button class="btn-primary" @click="$emit('close')">Fechar</button>
        </footer>
      </aside>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'FiltersDrawer',
  props: {
    visible: { type: Boolean, default: false },
    filters: { type: Object, required: true },
    sections: { type: Array, required: true }
    // sections: [{ key, title, options: [{ value, label, count? }] }, ...]
  },
  emits: ['update:filters', 'close'],
  methods: {
    select(key, value) {
      this.$emit('update:filters', { ...this.filters, [key]: value })
    },
    clearAll() {
      const cleared = {}
      for (const s of this.sections) cleared[s.key] = 'ALL'
      this.$emit('update:filters', cleared)
    }
  },
  watch: {
    visible(open) {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : ''
      }
    }
  },
  beforeUnmount() {
    if (typeof document !== 'undefined') document.body.style.overflow = ''
  }
}
</script>

<style scoped>
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 150;
  display: flex;
  justify-content: flex-end;
}
.drawer {
  width: 100%;
  max-width: 380px;
  height: 100%;
  background: var(--color-bg-surface);
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--color-bg-separator);
}
.drawer-title { font-size: var(--text-lg); font-weight: 800; margin: 0; color: var(--color-text-primary); }
.drawer-close {
  width: 32px; height: 32px;
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: none; cursor: pointer;
  color: var(--color-text-muted);
  border-radius: 50%;
  transition: var(--transition-fast);
}
.drawer-close:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 20px 20px;
}

.filter-section {
  padding: 16px 0;
  border-bottom: 1px solid var(--color-bg-separator);
}
.filter-section:last-child { border-bottom: none; }
.filter-section-title {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--color-text-muted);
  margin: 0 0 10px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 8px;
  margin: 0 -8px;
  border-radius: var(--radius-md);
  cursor: pointer;
  user-select: none;
  font-size: var(--text-sm);
  color: var(--color-text-primary);
  transition: background 0.15s;
}
.filter-option:hover { background: var(--color-bg-hover); }
.filter-option input { display: none; }

.opt-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #ccc;
  flex-shrink: 0;
  position: relative;
  transition: var(--transition-fast);
}
.filter-option.active .opt-dot {
  border-color: var(--color-accent);
}
.filter-option.active .opt-dot::after {
  content: '';
  position: absolute;
  inset: 3px;
  background: var(--color-accent);
  border-radius: 50%;
}
.opt-label { flex: 1; font-weight: 500; }
.filter-option.active .opt-label { font-weight: 700; color: var(--color-accent); }

.opt-count {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-muted);
  background: var(--color-bg-hover);
  padding: 2px 8px;
  border-radius: 10px;
  min-width: 24px;
  text-align: center;
}
.filter-option.active .opt-count {
  background: rgba(51, 102, 204, 0.12);
  color: var(--color-accent);
}

.drawer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid var(--color-bg-separator);
  gap: 12px;
}
.btn-link {
  background: none;
  border: none;
  color: var(--color-danger);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  padding: 8px 4px;
}
.btn-link:hover { text-decoration: underline; }

/* Animação */
.drawer-enter-active,
.drawer-leave-active { transition: background 0.25s ease; }
.drawer-enter-active .drawer,
.drawer-leave-active .drawer { transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.3, 1); }
.drawer-enter-from .drawer { transform: translateX(100%); }
.drawer-leave-to .drawer { transform: translateX(100%); }
.drawer-enter-from,
.drawer-leave-to { background: rgba(0, 0, 0, 0); }

@media (max-width: 500px) {
  .drawer { max-width: 100%; }
}
</style>
